import type { Configuration, PopupRequest, SilentRequest } from '@azure/msal-browser';
import { LogLevel, PublicClientApplication } from '@azure/msal-browser';
import type { SecurityGroupConfig } from 'types/auth';
import { AppRole } from 'types/auth';

// ─── MSAL Configuration ───
// These values should be set via environment variables in production
// For local development, set these in .env.local

const MSAL_CLIENT_ID = import.meta.env.REACT_APP_MSAL_CLIENT_ID || '';
const MSAL_TENANT_ID = import.meta.env.REACT_APP_MSAL_TENANT_ID || '';
const MSAL_REDIRECT_URI = import.meta.env.REACT_APP_MSAL_REDIRECT_URI || window.location.origin;
const MSAL_API_SCOPE = import.meta.env.REACT_APP_MSAL_API_SCOPE || '';

// Security group IDs for role mapping
const SOC_OPERATORS_GROUP_ID = import.meta.env.REACT_APP_SOC_OPERATORS_GROUP_ID || '';
const SOC_ADMIN_GROUP_ID = import.meta.env.REACT_APP_SOC_ADMIN_GROUP_ID || '';

/**
 * Security group configuration for role mapping
 */
export const securityGroupConfig: SecurityGroupConfig = {
  operatorsGroupId: SOC_OPERATORS_GROUP_ID,
  adminGroupId: SOC_ADMIN_GROUP_ID,
};

/**
 * Map security group IDs to application roles
 */
export function mapGroupsToAppRoles(groupIds: string[]): AppRole[] {
  const roles: AppRole[] = [];

  if (groupIds.includes(securityGroupConfig.adminGroupId)) {
    roles.push(AppRole.Admin);
  }
  if (groupIds.includes(securityGroupConfig.operatorsGroupId)) {
    roles.push(AppRole.Operator);
  }

  return roles;
}

/**
 * Check if user has app access based on security group membership
 */
export function hasAppAccess(groupIds: string[]): boolean {
  return (
    groupIds.includes(securityGroupConfig.operatorsGroupId) ||
    groupIds.includes(securityGroupConfig.adminGroupId)
  );
}

/**
 * MSAL configuration for Azure AD authentication.
 * Supports both browser and MS Teams embedded scenarios.
 */
export const msalConfig: Configuration = {
  auth: {
    clientId: MSAL_CLIENT_ID,
    authority: `https://login.microsoftonline.com/${MSAL_TENANT_ID}`,
    redirectUri: MSAL_REDIRECT_URI,
    postLogoutRedirectUri: MSAL_REDIRECT_URI,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Error, // Error only in production
      piiLoggingEnabled: false,
    },
  },
};

/**
 * Scopes required for API access
 */
export const apiScopes: string[] = MSAL_API_SCOPE ? [MSAL_API_SCOPE] : [];

/**
 * Login request configuration
 */
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'email', ...apiScopes],
};

/**
 * Silent token request configuration
 */
export const silentRequest: SilentRequest = {
  scopes: apiScopes,
  account: undefined as never, // Will be set when called
};

/**
 * Check if MSAL is properly configured
 */
export function isMsalConfigured(): boolean {
  return Boolean(MSAL_CLIENT_ID && MSAL_TENANT_ID);
}

/**
 * Shared MSAL instance - use this throughout the app
 */
export const msalInstance = new PublicClientApplication(msalConfig);
